// Initialize logger
const logger = createLogger('FormState');

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SectionKey, getWorkflowSectionKeys } from '../../../../types';
import { FormState, SectionProgress } from '../types/core/form-state.types';
import { createLogger } from '../../../../utils/logger';
import { PaymentWorkflowType } from '../types/api/graph.types';
import { getOfficeForWorkflowType } from '../../../../utils/workflowRegistry';
import { isFormRejected } from '../../../../utils/rejectionUtils';
import { getOvertimeSectionFlow, hasOvertimeMatterInGrid } from '../schemas/OvertimeApproval/utils';
import {
  getGiftHospitalitySectionFlow,
  GIFT_HOSPITALITY_APPROVAL_SECTION_KEYS,
  isGiftHospitalityWorkflow,
} from '../schemas/GiftHospitalityDonation/authoriserRules';

// Helper function to check if form is cancelled
const isFormCancelled = (formData: Record<string, unknown>): boolean => {
  // Check if the workflow status indicates cancellation
  if (formData?.currentAuthStage === 'Cancelled') {
    return true;
  }

  // Check if there's a specific cancelled flag
  if (formData?.isCancelled === true) {
    return true;
  }

  return false;
};

// Base section flow without conditional sections
const BASE_SECTION_FLOW: SectionKey[] = [
  'payment_details',
  'resp_lawyer',
  'first_partner',
  'second_partner',
  'payment_confirmation',
  'document_saving',
];

// Function to generate dynamic section flow based on form data
export const getSectionFlow = (
  formData: Record<string, any> = {}
): SectionKey[] => {
  const flow = [...BASE_SECTION_FLOW];

  // Add payment_hold_review section if resp_hold_payment is 'yes'
  if (formData.resp_hold_payment === 'yes') {
    // Insert payment_hold_review before payment_confirmation
    const paymentConfirmationIndex = flow.indexOf('payment_confirmation');
    flow.splice(paymentConfirmationIndex, 0, 'payment_hold_review');
  }

  // Determine total payment amount
  // First check if payment_total is directly set (for 5+ payments mode)
  const directPaymentTotal = parseFloat(formData.payment_total) || 0;
  let paymentTotal = directPaymentTotal;

  // If no direct payment total, calculate from gridData (for 1-5 payments mode)
  if (paymentTotal === 0) {
    const gridData = formData?.gridData || [];
    const rowsToProcess = Array.isArray(
      (gridData as Record<string, unknown>).data
    )
      ? (gridData as Record<string, unknown>).data
      : Array.isArray(gridData)
      ? gridData
      : [];

    paymentTotal = (rowsToProcess as any[]).reduce(
      (total: number, row: any) => {
        const amount = parseFloat(row?.amount as any) || 0;
        return total + amount;
      },
      0
    );
  }

  // Determine if any second partner data is present (optional under 25k)
  const hasSecondPartnerData = (() => {
    // Check if any field starting with 'second_' has a meaningful value
    const keys = Object.keys(formData || {});
    const hasSecondPrefixedValue = keys.some((key) => {
      if (!key.startsWith('second_')) return false;
      const value = (formData as any)[key];
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;
      return Boolean(value);
    });

    // Also respect explicit assignment of the section to the user, if present
    const assignedSections = Array.isArray(formData?.userAssignedSections)
      ? (formData.userAssignedSections as string[])
      : [];
    const isSectionExplicitlyAssigned =
      assignedSections.includes('second_partner');

    return hasSecondPrefixedValue || isSectionExplicitlyAssigned;
  })();

  // Remove second_partner section if payment is under 25000 AND no second partner data provided
  if (paymentTotal < 25000 && !hasSecondPartnerData) {
    const secondPartnerIndex = flow.indexOf('second_partner');
    if (secondPartnerIndex !== -1) {
      flow.splice(secondPartnerIndex, 1);
    }
  }

  return flow;
};

// Default section flow for initial state
export const SECTION_FLOW: SectionKey[] = getSectionFlow();

const INITIAL_SECTION_PROGRESS: SectionProgress = BASE_SECTION_FLOW.reduce(
  (acc, section) => ({
    ...acc,
    [section]: {
      isComplete: false,
      completedDate: undefined,
    },
  }),
  {} as SectionProgress
);

export const INITIAL_FORM_STATE: FormState = {
  data: {
    location: 'nonUk', // Default location for all workflows
  },
  errors: {},
  isSubmitting: false,
  isDirty: false,
  isComplete: false,
  completedSections: [],
  submittedSections: [],
  currentSection: 'payment_details',
  sectionValidation: BASE_SECTION_FLOW.reduce(
    (acc, section) => ({
      ...acc,
      [section]: false,
    }),
    {} as Record<SectionKey, boolean>
  ),
  sectionProgress: INITIAL_SECTION_PROGRESS,
  isLoading: true,
  workflowType: 0,
  office: 'M1', // Default office
  isRejected: false,
  isCancelled: false,
};

// Function to create initial form state based on workflow type
export const createInitialFormState = (
  workflowType: PaymentWorkflowType
): FormState => {
  // Get the section flow for this workflow type
  const sectionFlow = getWorkflowSectionKeys(workflowType);

  // Create section validation based on the section flow
  const sectionValidation = sectionFlow.reduce(
    (acc: Record<SectionKey, boolean>, section: SectionKey) => ({
      ...acc,
      [section]: false,
    }),
    {} as Record<SectionKey, boolean>
  );

  // Create section progress based on the section flow
  const sectionProgress = sectionFlow.reduce(
    (acc: SectionProgress, section: SectionKey) => ({
      ...acc,
      [section]: {
        isComplete: false,
        completedDate: undefined,
      },
    }),
    {} as SectionProgress
  );

  // Determine the initial current section based on workflow type
  let currentSection: SectionKey = 'payment_details';

  if (workflowType === PaymentWorkflowType.DubaiTransferRequisition) {
    currentSection = 'dubai_transfer_requisition_payment_details';
  } else if (workflowType === PaymentWorkflowType.DubaiPaymentFromOfficeAcc) {
    currentSection = 'dubai_payment_from_office_account_payment_details';
  } else if (workflowType === PaymentWorkflowType.OvertimeApproval) {
    currentSection = 'overtime_approval_request_details';
  }

  // Get office from workflow registry
  const office = getOfficeForWorkflowType(workflowType);

  return {
    data: {
      location: 'nonUk', // Default location for all workflows
    },
    errors: {},
    isSubmitting: false,
    isDirty: false,
    isComplete: false,
    completedSections: [],
    submittedSections: [],
    currentSection,
    sectionValidation,
    sectionProgress,
    isLoading: true,
    workflowType,
    office,
    isRejected: false,
    isCancelled: false,
  };
};

export interface SectionProgressSummary {
  total: number;
  completed: number;
  percentage: number;
  currentSectionComplete: boolean;
  completedSections: {
    [K in SectionKey]?: boolean;
  };
}

export const useFormState = (initialData = {}) => {
  const [formState, setFormState] = useState<FormState>({
    ...INITIAL_FORM_STATE,
    data: initialData,
  });

  // Track previous section flow to detect changes
  const [previousSectionFlow, setPreviousSectionFlow] =
    useState<SectionKey[]>(BASE_SECTION_FLOW);

  // Track previous payment total to avoid infinite loops
  const previousPaymentTotalRef = useRef<number | undefined>(undefined);
  const previousOvertimeHasMatterRef = useRef<boolean | undefined>(undefined);

  // Get current section flow based on workflow type and form data.
  // Dubai Payment Approval uses getSectionFlow so conditional sections
  // (e.g. payment_hold_review when resp_hold_payment === 'yes') are applied.
  // Transfer Requisition excludes second partner unless Client → Client.
  const getCurrentSectionFlow = useCallback((): SectionKey[] => {
    if (formState.workflowType === PaymentWorkflowType.DubaiPaymentApproval) {
      return getSectionFlow(formState.data);
    }

    const flow = getWorkflowSectionKeys(formState.workflowType);

    if (
      formState.workflowType === PaymentWorkflowType.DubaiTransferRequisition
    ) {
      const isClientToClient =
        formState.data?.debitType === 'client' &&
        formState.data?.creditType === 'client';
      if (!isClientToClient) {
        return flow.filter(
          (section) => section !== 'dubai_transfer_requisition_second_partner'
        );
      }
    }

    if (formState.workflowType === PaymentWorkflowType.OvertimeApproval) {
      return getOvertimeSectionFlow(formState.data) as SectionKey[];
    }

    if (isGiftHospitalityWorkflow(formState.workflowType)) {
      return getGiftHospitalitySectionFlow(
        flow,
        formState.data
      ) as SectionKey[];
    }

    return flow;
  }, [formState.workflowType, formState.data]);

  const updateFormData = useCallback(
    (update: Partial<FormState> | Record<string, any>) => {
      logger.update('Updating form data', {
        // Remove formState reference here since it causes the dependency
        update,
        source: new Error().stack,
      });

      setFormState((prev) => {
        // Log current state here instead
        logger.update('Updating form data', {
          currentState: prev, // Use prev instead of formState
          update,
          source: new Error().stack,
        });

        const topLevelKeys = Object.keys(prev);
        const hasTopLevelKey = Object.keys(update).some((key) =>
          topLevelKeys.includes(key)
        );

        let newState;
        if (hasTopLevelKey) {
          const updatedData = { ...prev.data };
          newState = {
            ...prev,
            ...update,
            isDirty: true,
            isRejected: isFormRejected(
              updatedData,
              prev.completedSections,
              prev.submittedSections
            ),
            isCancelled: isFormCancelled(updatedData),
          };
        } else {
          const updatedData = { ...prev.data, ...update };
          newState = {
            ...prev,
            data: updatedData,
            isDirty: true,
            isRejected: isFormRejected(
              updatedData,
              prev.completedSections,
              prev.submittedSections
            ),
            isCancelled: isFormCancelled(updatedData),
          };
        }

        logger.state('Form state updated', {
          previous: prev,
          new: newState,
          diff: Object.keys(update),
        });

        return newState;
      });
    },
    [] // Empty dependency array
  );

  const setErrors = useCallback(
    (
      errors:
        | Record<string, string>
        | ((prevErrors: Record<string, string>) => Record<string, string>)
    ) => {
      setFormState((prev) => ({
        ...prev,
        errors: typeof errors === 'function' ? errors(prev.errors) : errors,
      }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormState(INITIAL_FORM_STATE);
  }, []);

  const resetRejectionStatus = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      isRejected: false,
    }));
  }, []);

  const completeSection = useCallback(
    (section: SectionKey) => {
      logger.update('Starting section completion', {
        section,
        currentProgress: formState.sectionProgress[section],
      });

      setFormState((prev) => {
        // Create new completed sections array
        const newCompletedSections = Array.from(
          new Set([...prev.completedSections, section])
        );

        // Create new section progress
        const newSectionProgress = {
          ...prev.sectionProgress,
          [section]: {
            isComplete: true,
            completedDate: new Date().toISOString(),
          },
        };

        // Create new validation state
        const newSectionValidation = {
          ...prev.sectionValidation,
          [section]: true,
        };

        const newState = {
          ...prev,
          completedSections: newCompletedSections,
          sectionProgress: newSectionProgress,
          sectionValidation: newSectionValidation,
          isDirty: true,
        };

        logger.state('Section completion state updated', {
          section,
          previousState: prev,
          newState,
          changes: {
            completedSections: newCompletedSections,
            sectionProgress: newState.sectionProgress[section],
            validation: newState.sectionValidation[section],
          },
        });

        return newState;
      });
    },
    [formState.sectionProgress]
  );

  const submitSection = useCallback((section: SectionKey) => {
    logger.update('Section submission initiated', {
      section,
      trigger: new Error().stack?.split('\n')[2],
    });

    setFormState((prev) => {
      // Add section to submitted sections if not already there
      const newSubmittedSections = prev.submittedSections.includes(section)
        ? prev.submittedSections
        : [...prev.submittedSections, section];

      const newState = {
        ...prev,
        submittedSections: newSubmittedSections,
        isDirty: true,
        // Recalculate rejection status with updated submitted sections
        isRejected: isFormRejected(
          prev.data,
          prev.completedSections,
          newSubmittedSections
        ),
      };

      logger.state('Section submission state updated', {
        section,
        previousState: prev,
        newState,
        changes: {
          submittedSections: newSubmittedSections,
          isRejected: newState.isRejected,
        },
      });

      return newState;
    });
  }, []);

  const updateSectionProgress = useCallback(
    (sectionKey: SectionKey, isComplete: boolean, completedDate?: string) => {
      logger.update('Section progress update initiated', {
        section: sectionKey,
        isComplete,
        completedDate,
        trigger: new Error().stack?.split('\n')[2], // Shows where the update was triggered from
      });

      setFormState((prev) => {
        const previousSectionState = prev.sectionProgress[sectionKey];
        const newCompletedDate =
          completedDate || (isComplete ? new Date().toISOString() : null);

        const newState = {
          ...prev,
          sectionProgress: {
            ...prev.sectionProgress,
            [sectionKey]: {
              isComplete,
              completedDate: newCompletedDate,
            },
          },
        };

        logger.state('Section progress updated', {
          section: sectionKey,
          changes: {
            from: {
              isComplete: previousSectionState?.isComplete,
              completedDate: previousSectionState?.completedDate,
            },
            to: {
              isComplete,
              completedDate: newCompletedDate,
            },
          },
          fullState: {
            previous: prev.sectionProgress,
            new: newState.sectionProgress,
          },
          affectedSection: sectionKey,
          timestamp: new Date().toISOString(),
        });

        // Log warning if section status is being changed from complete to incomplete
        if (previousSectionState?.isComplete && !isComplete) {
          logger.warning('Section status changed from complete to incomplete', {
            section: sectionKey,
            previousState: previousSectionState,
            newState: newState.sectionProgress[sectionKey],
          });
        }

        return newState;
      });
    },
    []
  );

  const setCurrentSection = useCallback((section: SectionKey) => {
    setFormState((prev) => ({
      ...prev,
      currentSection: section,
    }));
  }, []);

  const canProceedToSection = useCallback(
    (section: SectionKey) => {
      logger.update('Checking section navigation', { targetSection: section });

      // Block navigation when form is rejected
      if (formState.isRejected) {
        logger.warning('Navigation blocked: form is rejected');
        return false;
      }

      // Block navigation when form is cancelled
      if (formState.isCancelled) {
        logger.warning('Navigation blocked: form is cancelled');
        return false;
      }

      if (!section) {
        logger.warning('Invalid section provided', { section });
        return false;
      }

      const currentSectionFlow = getCurrentSectionFlow();
      const sectionIndex = currentSectionFlow.indexOf(section);
      if (sectionIndex === -1) {
        logger.error('Section not found in flow', {
          section,
          currentSectionFlow,
        });
        return false;
      }

      const previousSections = currentSectionFlow.slice(0, sectionIndex);
      const result = previousSections.every((prevSection) => {
        const sectionStatus = formState.sectionProgress[prevSection];
        const isComplete = Boolean(sectionStatus?.isComplete);

        logger.state(`Checking previous section: ${prevSection}`, {
          status: sectionStatus,
          isComplete,
        });

        return isComplete;
      });

      logger.state('Navigation check complete', {
        targetSection: section,
        previousSections,
        result,
        currentProgress: formState.sectionProgress,
        currentSectionFlow,
      });

      return result;
    },
    [
      formState.sectionProgress,
      formState.isRejected,
      formState.isCancelled,
      getCurrentSectionFlow,
    ]
  );

  const updateSectionState = useCallback(
    (
      currentSection: SectionKey,
      nextSection: SectionKey | null,
      isComplete: boolean = true
    ) => {
      logger.update('Updating section state', {
        currentSection,
        nextSection,
        isComplete,
        timestamp: new Date().toISOString(),
      });

      setFormState((prev) => {
        const newState = {
          ...prev,
          isSubmitting: false,
          isDirty: false,
          errors: {},
          currentSection: nextSection || currentSection,
          completedSections: isComplete
            ? Array.from(new Set([...prev.completedSections, currentSection]))
            : prev.completedSections,
          sectionProgress: {
            ...prev.sectionProgress,
            [currentSection]: {
              isComplete,
              completedDate: isComplete ? new Date().toISOString() : null,
            },
          },
          sectionValidation: {
            ...prev.sectionValidation,
            [currentSection]: isComplete,
          },
        };

        logger.state('Section state updated', {
          previous: prev,
          new: newState,
          changes: {
            section: currentSection,
            completionStatus: isComplete,
            nextSection: nextSection || currentSection,
          },
        });

        return newState;
      });
    },
    []
  );

  const sectionProgress = useMemo((): SectionProgressSummary => {
    const currentSectionFlow = getCurrentSectionFlow();

    const completedSections = Object.entries(formState.sectionProgress).reduce(
      (acc, [section, status]) => {
        if (status.isComplete) {
          acc[section as SectionKey] = true;
        }
        return acc;
      },
      {} as { [K in SectionKey]?: boolean }
    );

    // Only count completed sections that are in the current flow
    const completedCount = currentSectionFlow.reduce((count, section) => {
      const sectionStatus = formState.sectionProgress[section];
      return count + (sectionStatus?.isComplete ? 1 : 0);
    }, 0);

    return {
      total: currentSectionFlow.length,
      completed: completedCount,
      percentage: (completedCount / currentSectionFlow.length) * 100,
      currentSectionComplete:
        formState.sectionProgress[formState.currentSection]?.isComplete ??
        false,
      completedSections,
    };
  }, [
    formState.sectionProgress,
    formState.currentSection,
    getCurrentSectionFlow,
  ]);

  // Handle section flow changes when form data changes
  const handleSectionFlowChange = useCallback(() => {
    const currentFlow = getCurrentSectionFlow();
    const previousFlow = previousSectionFlow;

    // Handle payment_hold_review section changes
    const wasPaymentHoldInPreviousFlow = previousFlow.includes(
      'payment_hold_review'
    );
    const isPaymentHoldInCurrentFlow = currentFlow.includes(
      'payment_hold_review'
    );

    if (wasPaymentHoldInPreviousFlow && !isPaymentHoldInCurrentFlow) {
      setFormState((prev) => ({
        ...prev,
        sectionProgress: {
          ...prev.sectionProgress,
          payment_hold_review: {
            isComplete: false,
            completedDate: undefined,
          },
        },
        sectionValidation: {
          ...prev.sectionValidation,
          payment_hold_review: false,
        },
        completedSections: prev.completedSections.filter(
          (section) => section !== 'payment_hold_review'
        ),
      }));
    } else if (!wasPaymentHoldInPreviousFlow && isPaymentHoldInCurrentFlow) {
      setFormState((prev) => ({
        ...prev,
        sectionProgress: {
          ...prev.sectionProgress,
          payment_hold_review: {
            isComplete: false,
            completedDate: undefined,
          },
        },
        sectionValidation: {
          ...prev.sectionValidation,
          payment_hold_review: false,
        },
      }));
    }

    // Handle second_partner section changes
    const wasSecondPartnerInPreviousFlow =
      previousFlow.includes('second_partner');
    const isSecondPartnerInCurrentFlow = currentFlow.includes('second_partner');

    if (wasSecondPartnerInPreviousFlow && !isSecondPartnerInCurrentFlow) {
      setFormState((prev) => ({
        ...prev,
        sectionProgress: {
          ...prev.sectionProgress,
          second_partner: {
            isComplete: false,
            completedDate: undefined,
          },
        },
        sectionValidation: {
          ...prev.sectionValidation,
          second_partner: false,
        },
        completedSections: prev.completedSections.filter(
          (section) => section !== 'second_partner'
        ),
      }));
    } else if (
      !wasSecondPartnerInPreviousFlow &&
      isSecondPartnerInCurrentFlow
    ) {
      setFormState((prev) => ({
        ...prev,
        sectionProgress: {
          ...prev.sectionProgress,
          second_partner: {
            isComplete: false,
            completedDate: undefined,
          },
        },
        sectionValidation: {
          ...prev.sectionValidation,
          second_partner: false,
        },
      }));
    }

    const financeSection = 'overtime_approval_finance_confirmation';
    const wasFinanceInPreviousFlow = previousFlow.includes(financeSection);
    const isFinanceInCurrentFlow = currentFlow.includes(financeSection);

    if (wasFinanceInPreviousFlow && !isFinanceInCurrentFlow) {
      setFormState((prev) => ({
        ...prev,
        sectionProgress: {
          ...prev.sectionProgress,
          [financeSection]: {
            isComplete: false,
            completedDate: undefined,
          },
        },
        sectionValidation: {
          ...prev.sectionValidation,
          [financeSection]: false,
        },
        completedSections: prev.completedSections.filter(
          (section) => section !== financeSection
        ),
      }));
    } else if (!wasFinanceInPreviousFlow && isFinanceInCurrentFlow) {
      setFormState((prev) => ({
        ...prev,
        sectionProgress: {
          ...prev.sectionProgress,
          [financeSection]: {
            isComplete: false,
            completedDate: undefined,
          },
        },
        sectionValidation: {
          ...prev.sectionValidation,
          [financeSection]: false,
        },
      }));
    }

    GIFT_HOSPITALITY_APPROVAL_SECTION_KEYS.forEach((approvalSection) => {
      const wasApprovalInPreviousFlow = previousFlow.includes(approvalSection);
      const isApprovalInCurrentFlow = currentFlow.includes(approvalSection);

      if (wasApprovalInPreviousFlow && !isApprovalInCurrentFlow) {
        setFormState((prev) => ({
          ...prev,
          sectionProgress: {
            ...prev.sectionProgress,
            [approvalSection]: {
              isComplete: false,
              completedDate: undefined,
            },
          },
          sectionValidation: {
            ...prev.sectionValidation,
            [approvalSection]: false,
          },
          completedSections: prev.completedSections.filter(
            (section) => section !== approvalSection
          ),
        }));
      }
    });

    // Update the previous section flow for next comparison
    setPreviousSectionFlow(currentFlow);
  }, [getCurrentSectionFlow, previousSectionFlow]);

  // Handle section flow changes when gridData or payment_total changes
  useEffect(() => {
    // First check if payment_total is directly set (for 5+ payments mode)
    const directPaymentTotal = parseFloat(formState.data?.payment_total) || 0;
    let currentPaymentTotal = directPaymentTotal;

    // If no direct payment total, calculate from gridData (for 1-5 payments mode)
    if (currentPaymentTotal === 0) {
      const gridData = formState.data?.gridData || [];
      const rowsToProcess = Array.isArray(
        (gridData as Record<string, unknown>).data
      )
        ? (gridData as Record<string, unknown>).data
        : Array.isArray(gridData)
        ? gridData
        : [];

      currentPaymentTotal = (rowsToProcess as any[]).reduce(
        (total: number, row: any) => {
          const amount = parseFloat(row.amount) || 0;
          return total + amount;
        },
        0
      );
    }

    const previousPaymentTotal = previousPaymentTotalRef.current;

    // Only trigger if payment total has changed and has a meaningful value
    if (
      currentPaymentTotal !== previousPaymentTotal &&
      currentPaymentTotal > 0
    ) {
      previousPaymentTotalRef.current = currentPaymentTotal;
      handleSectionFlowChange();
    }
  }, [
    formState.data?.gridData,
    formState.data?.payment_total,
    handleSectionFlowChange,
  ]);

  useEffect(() => {
    if (formState.workflowType !== PaymentWorkflowType.OvertimeApproval) {
      return;
    }

    const hasMatter = hasOvertimeMatterInGrid(formState.data);
    if (previousOvertimeHasMatterRef.current === hasMatter) {
      return;
    }

    previousOvertimeHasMatterRef.current = hasMatter;
    handleSectionFlowChange();
  }, [
    formState.workflowType,
    formState.data,
    handleSectionFlowChange,
  ]);

  return {
    formState,
    updateFormData,
    setErrors,
    resetForm,
    resetRejectionStatus,
    completeSection,
    submitSection,
    setCurrentSection,
    canProceedToSection,
    sectionProgress,
    updateSectionProgress,
    updateSectionState,
    getCurrentSectionFlow,
    handleSectionFlowChange,
  };
};
